import { useState } from 'react'
import { useAdminFetch } from '../hooks/useAdminAPI'
import {
  PageHeader, AdminButton, StatusBadge,
  MetricStrip, Metric, Section, DataTable,
  SegmentedControl, Notice, ViewState,
} from '../components'
import styles from './Offers.module.css'

export function Offers() {
  const [filter, setFilter] = useState('all')
  const deals = useAdminFetch('/deals?limit=100')
  const offers = deals.data?.deals ?? []

  if (deals.loading && !deals.data) return <ViewState variant="loading" />
  if (deals.error && !deals.data) return <ViewState variant="error" message={deals.error} onRetry={deals.refresh} />

  const filtered = filterOffers(offers, filter)
  const warningCount = offers.filter(o => o.comparisonBasis === 'insufficient-unit-data').length
  const suspiciousCount = offers.filter(o => o.dealScore != null && (o.dealScore > 50 || o.dealScore < -30)).length
  const zeroDealCount = offers.filter(o => o.dealScore === 0).length
  const withDealCount = offers.filter(o => o.dealScore != null && o.dealScore !== 0).length

  const isFreshSystem = zeroDealCount > offers.length * 0.6

  return (
    <div className={styles.page}>
      <PageHeader
        title="Offers"
        subtitle={`${offers.length} current offers`}
        actions={
          <AdminButton
            variant="icon-only"
            icon="refresh"
            label="Refresh data"
            onClick={deals.refresh}
            disabled={deals.loading}
          />
        }
      />

      <MetricStrip>
        <Metric label="Total offers" value={offers.length} />
        <Metric label="With deal scores" value={withDealCount} variant="success" />
        <Metric
          label="Missing unit data"
          value={warningCount}
          variant={warningCount > 0 ? 'warning' : 'success'}
        />
        <Metric
          label="Suspicious"
          value={suspiciousCount}
          variant={suspiciousCount > 0 ? 'error' : 'success'}
        />
      </MetricStrip>

      {isFreshSystem && (
        <Notice variant="info" title="Limited price history">
          Most offers show 0% because there's only 1 week of data. Deal scores compare against the 90-day average, so they'll become meaningful after 2–3 weeks of scraping.
        </Notice>
      )}

      <SegmentedControl
        value={filter}
        onChange={setFilter}
        options={[
          { value: 'all', label: 'All' },
          { value: 'zero-deal', label: '0% deals', count: zeroDealCount },
          { value: 'warnings', label: 'Warnings', count: warningCount },
          { value: 'suspicious', label: 'Suspicious', count: suspiciousCount },
        ]}
      />

      {filtered.length === 0 ? (
        <ViewState variant="empty" message="No offers match this filter." />
      ) : (
        <DataTable>
          <thead>
            <tr>
              <th>Supermarket</th>
              <th>Product</th>
              <th>Cluster</th>
              <th>Unit</th>
              <th className={styles.numberCol}>Price</th>
              <th className={styles.numberCol}>Original</th>
              <th className={styles.numberCol}>€/kg</th>
              <th className={styles.numberCol}>Deal</th>
              <th>Basis</th>
              <th>Verdict</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((offer, i) => (
              <tr
                key={`${offer.supermarket}-${offer.productName}-${i}`}
                className={getRowClass(offer)}
              >
                <td className={styles.supermarketCell}>
                  <span className={`${styles.supermarketDot} ${styles[`sm_${offer.supermarket}`]}`} />
                  {offer.supermarket.toUpperCase()}
                </td>
                <td>{offer.productName}</td>
                <td className={styles.tertiary}>{offer.clusterName}</td>
                <td><UnitBadge offer={offer} /></td>
                <td className={styles.number}>€{offer.price?.toFixed(2)}</td>
                <td className={styles.number}>
                  {offer.originalPrice ? `€${offer.originalPrice.toFixed(2)}` : '—'}
                </td>
                <td className={styles.number}>
                  {offer.pricePerKg ? `€${offer.pricePerKg.toFixed(2)}` : '—'}
                </td>
                <td className={`${styles.number} ${getDealClass(offer.dealScore)}`}>
                  <DealCell score={offer.dealScore} avg={offer.avg90d} />
                </td>
                <td><BasisBadge basis={offer.comparisonBasis} /></td>
                <td><VerdictBadge verdict={offer.verdict} /></td>
              </tr>
            ))}
          </tbody>
        </DataTable>
      )}

      <div className={styles.legend}>
        <div className={styles.legendItem}>
          <span className={styles.basisBadge}>price-per-kg</span>
          <span className={styles.tertiary}>= Compared by €/kg (most accurate)</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.basisBadge} ${styles.basisPiece}`}>raw-price</span>
          <span className={styles.tertiary}>= Per-piece comparison (no weight available)</span>
        </div>
        <div className={styles.legendItem}>
          <span className={`${styles.basisBadge} ${styles.basisWarning}`}>insufficient-unit-data</span>
          <span className={styles.tertiary}>= Unit not recognized, can't compare</span>
        </div>
      </div>
    </div>
  )
}

function UnitBadge({ offer }) {
  const unit = offer.unit
  if (!unit) return <span className={styles.tertiary}>—</span>

  const isUnknown = offer.unitMeasurement === 'unknown'
  return (
    <span
      className={isUnknown ? styles.unitUnknown : styles.unitKnown}
      title={`measurement: ${offer.unitMeasurement ?? 'n/a'}, kg: ${offer.unitKg ?? 'n/a'}`}
    >
      {unit}
    </span>
  )
}

function DealCell({ score, avg }) {
  if (score == null) return <span className={styles.tertiary}>—</span>
  if (score === 0 && avg) {
    return <span className={styles.tertiary} title={`Avg: €${avg.toFixed(2)} — same as current price (only 1 data point)`}>0%</span>
  }
  return <>{score}%</>
}

function BasisBadge({ basis }) {
  if (!basis) return <span className={styles.tertiary}>—</span>

  const isWarning = basis === 'insufficient-unit-data'
  const isPiece = basis === 'raw-price'
  const className = isWarning ? styles.basisWarning : isPiece ? styles.basisPiece : ''

  return <span className={`${styles.basisBadge} ${className}`}>{basis}</span>
}

function VerdictBadge({ verdict }) {
  const map = {
    great:     { className: styles.verdictGreat, label: 'great' },
    good:      { className: styles.verdictGood, label: 'good' },
    normal:    { className: styles.verdictNormal, label: 'normal' },
    expensive: { className: styles.verdictExpensive, label: 'expensive' },
    unknown:   { className: styles.verdictUnknown, label: 'unknown' },
  }
  const info = map[verdict] ?? map.unknown
  return <span className={`${styles.verdict} ${info.className}`}>{info.label}</span>
}

function filterOffers(offers, filter) {
  if (filter === 'warnings') {
    return offers.filter(o => o.comparisonBasis === 'insufficient-unit-data')
  }
  if (filter === 'suspicious') {
    return offers.filter(o => o.dealScore != null && (o.dealScore > 50 || o.dealScore < -30))
  }
  if (filter === 'zero-deal') {
    return offers.filter(o => o.dealScore === 0)
  }
  return offers
}

function getRowClass(offer) {
  if (offer.comparisonBasis === 'insufficient-unit-data') return styles.rowWarning
  if (offer.dealScore != null && (offer.dealScore > 50 || offer.dealScore < -30)) return styles.rowSuspicious
  return ''
}

function getDealClass(score) {
  if (score == null) return styles.tertiary
  if (score >= 20) return styles.dealGreat
  if (score >= 5) return styles.dealGood
  if (score < -5) return styles.dealBad
  return ''
}
